"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Support() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headline = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const body = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const ctas = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const sideLabel = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const badge = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const callouts = [{
    title: "Expert Supervision"
  }, {
    title: "Flexible Scheduling"
  }, {
    title: "Comprehensive Parenting Education"
  }];
  const audiences = [{
    title: "Families",
    description: "Parents and guardians seeking safe, child-centered contact solutions."
  }, {
    title: "Professionals",
    description: "Attorneys, social workers, and court staff requiring neutral documentation."
  }];
  return <section data-section-id="4913" id="arizona-family-support" className="relative w-full min-h-screen bg-background overflow-hidden dark bg-background text-foreground">
      {}
      <div className="lg:hidden w-full h-[60vh] relative overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_mobile.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_mobile.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_mobile.webp-wvc-sizes" alt="Professional family support services in Arizona" loading="lazy" decoding="async" className="w-full object-cover !h-[450px]" src="https://tinytoteenservices.org/wp-content/uploads/2026/06/IMG_0072.webp" />
        <div className="absolute inset-0 bg-foreground/5 mix-blend-multiply pointer-events-none" />
        <div className="absolute bottom-6 left-6 px-4 py-3 rounded-lg bg-background/90 backdrop-blur-sm flex flex-col items-center justify-center border border-border">
          <span className="font-serif italic text-[10px] uppercase tracking-wider text-foreground">PROFESSIONAL</span>
          <span className="font-serif italic text-sm text-foreground">TRUST</span>
          <span className="font-mono text-[10px] tracking-widest text-muted-foreground mt-1">AZ</span>
          <div className="mt-3 pt-3 border-t border-border w-full text-center">
            <p className="font-mono text-[8px] leading-tight text-muted-foreground uppercase tracking-tighter">
              Fully insured | General & Professional Liability Coverage $1M per occurrence / $1M aggregate | Arizona Statewide Service Area
            </p>
          </div>
        </div>
      </div>

      {}
      <div className="relative w-full min-h-screen lg:min-h-screen flex flex-col lg:flex-row">
        {}
        <div className="relative w-full lg:w-[58%] min-h-[80vh] lg:min-h-screen flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-24 py-16 lg:py-0">
          {}
          <div ref={eyebrow.ref} className={`transition-all duration-1000 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.25em] text-muted-foreground">
              § 01 — TINY TO TEEN SUPERVISED PARENTING & EDUCATION SERVICES
            </p>
            <div className="mt-4 w-12 h-px bg-foreground/30" />
          </div>

          {}
          <h1 ref={headline.ref} className={`font-default font-light text-foreground mt-8 lg:mt-10 leading-[1.1] transition-all duration-1000 delay-150 ease-out ${headline.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl`} style={{
          letterSpacing: "-0.03em"
        }}>
            Safe, Neutral, Structured Family Support in Arizona
          </h1>

          {}
          <div ref={body.ref} className={`transition-all duration-1000 delay-300 ease-out ${body.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default mt-10 lg:mt-14 max-w-[500px] text-foreground/75 text-base sm:text-lg leading-relaxed">
              We provide professional, trauma-informed supervised parenting time and family support services across Arizona. <Link to="/AboutTinyToTeenPage" className="text-foreground font-medium underline underline-offset-4 hover:text-primary transition-colors">Learn about our approach</Link>. Our child-centered approach ensures safety and neutrality, delivering the meticulous court documentation and structured environment necessary for families navigating complex transitions.
            </p>
            
            <div className="mt-8 flex flex-wrap gap-3">
              {callouts.map((item, i) => <div key={i} data-index={i} className="bg-card/40 backdrop-blur-sm border border-border p-4 rounded-lg flex-1 min-w-[200px]">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-primary block mb-2">Feature {i + 1}</span>
                  <p className="font-default text-sm font-medium text-foreground">{item.title}</p>
                </div>)}
            </div>
          </div>

          {}
          <div ref={ctas.ref} className={`mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-500 ease-out ${ctas.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Button asChild size="lg" className="rounded-lg px-8 py-6 text-base font-default tracking-wide shadow-md bg-primary text-white">
              <Link to="/IntakePathPage">Start Intake</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-lg px-8 py-6 text-base font-default tracking-wide border-secondary bg-transparent text-foreground hover:bg-secondary/20">
              <Link to="/ContactPage">Contact Us</Link>
            </Button>
          </div>

          {}
          <div className={`mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 transition-all duration-1000 delay-700 ease-out ${ctas.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {audiences.map((audience, i) => <div key={i} data-index={i} className="p-6 border border-border rounded-lg bg-card/20 backdrop-blur-sm">
                <h3 className="font-serif text-xl text-foreground mb-2">{audience.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{audience.description}</p>
              </div>)}
          </div>

          {}
          <div ref={sideLabel.ref} className={`hidden lg:flex absolute bottom-12 left-16 xl:left-24 items-center gap-4 transition-all duration-1000 delay-700 ease-out ${sideLabel.isVisible ? "opacity-100" : "opacity-0"}`}>
            <div className="w-px h-16 bg-foreground/40" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground" style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)"
          }}>
              § 01 / ARIZONA BASED
            </span>
          </div>
        </div>

        {}
        <div ref={imageReveal.ref} className={`hidden lg:block relative w-[42%] min-h-screen overflow-hidden transition-all duration-1500 ease-out ${imageReveal.isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="absolute inset-0 w-full h-[110%]" style={{
          transform: `translateY(${scrollY * 0.15}px)`
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_hero.png-wvc-sizes" alt="Calm and professional family support environment" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            {}
            <div className="absolute inset-0 mix-blend-overlay opacity-30 pointer-events-none" style={{
            backgroundImage: "radial-gradient(hsla(22, 14%, 22%, 0.15) 1px, transparent 1px)",
            backgroundSize: "3px 3px"
          }} />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-foreground/10 pointer-events-none" />
          </div>

          {}
          <div ref={badge.ref} className={`absolute bottom-16 left-8 xl:left-12 z-10 w-48 xl:w-56 p-6 rounded-lg bg-background/95 backdrop-blur-sm border border-border flex flex-col items-center justify-center shadow-md transition-all duration-1000 delay-700 ease-out ${badge.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
            <span className="font-serif italic text-lg xl:text-xl text-foreground">
              Neutral
            </span>
            <div className="w-8 h-px bg-foreground/40 my-2" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground mb-4">
              SUPPORT
            </span>
            <div className="w-full pt-3 border-t border-border">
              <p className="font-mono text-[9px] leading-tight text-muted-foreground text-center uppercase tracking-tighter">
                Fully insured | General & Professional Liability Coverage $1M per occurrence / $1M aggregate | Arizona Statewide Service Area
              </p>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 z-20">
        <div className="relative w-px h-16 bg-foreground/20 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-lg" style={{
          backgroundColor: "var(--secondary)",
          animation: "scrollDot 2s ease-in-out infinite"
        }} />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Scroll
        </span>
      </div>

      <style>{`
        @keyframes scrollDot {
          0% { transform: translate(-50%, -100%); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translate(-50%, 6400%); opacity: 0; }
        }
      `}</style>
    </section>;
}